import { GetCallResponse } from '@distribute/shared/api-types/call';
import { useDispatch, useSelector } from 'react-redux';
import { FC } from 'react';

import {
  Table,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '../../../shared/ui/TableV2';

import {
  VideoCardPreview,
  VideoCardTablePreview,
  VideoStatus,
} from '../../../entities/video';
import {
  TimeAgo,
  Avatar,
  Tooltip,
  AvatarTooltipContent,
} from '../../../shared/ui';
import {
  checkIsMuxFailed,
  checkIsMuxLoading,
  checkIsMuxReady,
  formatMsToTimePassed,
  getMuxAssetImageByPolicy,
} from '@distribute/shared/utils';
import { redirectActions } from '../../../entities/history';
import { CallDropdown } from './CallDropdown';

import { authUserModel } from '../../../entities/auth-user';

type Props = {
  calls: GetCallResponse[];
};

export const CallsGrid: FC<Props> = ({ calls }) => {
  const user = useSelector(authUserModel.selectors.selectUserWithError);

  const dispatch = useDispatch();

  return (
    <div className="sm:-mx-1 grid gap-5 sm:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] grid-cols-[repeat(auto-fill,minmax(340px,1fr))]">
      {calls.map((call) => {
        const { userToTeam } = call;

        const owner = userToTeam;

        const isOwner = owner?.user?.id === user?.id;

        const duration = formatMsToTimePassed(
          (call.muxAsset?.data?.duration || 0) * 1000
        );

        return (
          <VideoCardPreview
            onClick={() =>
              dispatch(redirectActions.toCallPage({ id: call.id }))
            }
            Action={
              isOwner && (
                <div className="md:hidden">
                  <CallDropdown call={call} view="grid" />
                </div>
              )
            }
            PermissionStatus={
              <VideoStatus
                isTeamAccessible={!call.isPrivate}
                teamMembersCount={1}
              />
            }
            createdAt={call.createdAt}
            title={call.name}
            duration={duration}
            isLoading={checkIsMuxLoading(call.muxAsset)}
            isFailed={checkIsMuxFailed(call.muxAsset)}
            url={getMuxAssetImageByPolicy(call.muxAsset)}
          />
        );
      })}
    </div>
  );
};

export const CallsList: FC<Props> = ({ calls }) => {
  const user = useSelector(authUserModel.selectors.selectUserWithError);

  const dispatch = useDispatch();

  return (
    <div className="sm:-mx-3 overflow-x-auto -mx-2 px-2 pb-2 -mb-2">
      <Table className="table-fixed">
        <TableHeader>
          <TableHeaderCell className="w-60">Name</TableHeaderCell>
          <TableHeaderCell className="w-24">Duration</TableHeaderCell>
          <TableHeaderCell className="w-34">Permissions</TableHeaderCell>
          <TableHeaderCell className="w-24">Created</TableHeaderCell>
          <TableHeaderCell className="w-14">Owner</TableHeaderCell>
          <TableHeaderCell className="w-15" />
        </TableHeader>
        {calls.map((call) => {
          const { userToTeam } = call;

          const owner = userToTeam;

          const isOwner = owner?.user?.id === user?.id;

          const duration = formatMsToTimePassed(
            (call.muxAsset?.data?.duration || 0) * 1000
          );

          return (
            <>
              <TableRow key={call.id + 'gap'} className="h-2" />
              <TableRow
                key={call.id}
                onClick={() =>
                  dispatch(redirectActions.toCallPage({ id: call.id }))
                }
              >
                <TableCell>
                  <VideoCardTablePreview
                    isLoading={checkIsMuxReady(call.muxAsset)}
                    url={getMuxAssetImageByPolicy(call.muxAsset)}
                    name={call.name}
                  />
                </TableCell>
                <TableCell>{duration}</TableCell>
                <TableCell>
                  <VideoStatus
                    isTeamAccessible={!call.isPrivate}
                    teamMembersCount={1}
                  />
                </TableCell>
                <TableCell>
                  <TimeAgo datetime={call.createdAt} locale="my" />
                </TableCell>
                <TableCell>
                  <div className="flex justify-start">
                    <Tooltip
                      sideOffset={4}
                      className="px-3 py-2"
                      hideArrow
                      trigger={
                        <Avatar
                          className="border border-base-black/[0.08]"
                          isUserAvatar
                          displayName={owner?.user?.displayName || ''}
                          src={owner?.user?.photoUrl}
                          size="xs"
                        />
                      }
                    >
                      <AvatarTooltipContent
                        displayName={owner?.user?.displayName}
                        email={owner?.user?.email || ''}
                      />
                    </Tooltip>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {isOwner && (
                    <div className="flex justify-end md:hidden">
                      <CallDropdown call={call} view="list" />
                    </div>
                  )}
                </TableCell>
              </TableRow>
            </>
          );
        })}
      </Table>
    </div>
  );
};
